<template>
  <main id="main">
    <div v-if="loading" class="text-center p-5">
      <p>{{ t('loading') }}</p>
    </div>
    <div v-else-if="!teacher.id" class="text-center p-5">
      <p>{{ t('empty_list') }}</p>
    </div>
    <template v-else>
      <div class="bg_gradient_theme_to_right p-4 rounded_header position-sticky top-0">
        <div class="d-flex align-items-center justify-content-between mb-5">
          <div class="d-flex align-items-center">
            <img id="arrow_back_page" src="@/assets/imgs/back.svg" alt="" @click="$router.go(-1)">
            <p id="title_header_page">{{ t('teachers_profile') }}</p>
          </div>
          <img src="@/assets/imgs/share.svg" class="icon-size-3 cursor-pointer" alt="" @click="shareContent('profile', `${teacher.name} ${teacher.family}`)">
        </div>

        <div class="d-flex align-items-center justify-content-between mb-3">
          <div style="width: calc(100% - 14rem); height: 13rem" class="d-flex flex-column justify-content-around gap-3 overflow-hidden">
            <p class="font_larger bold">{{ teacher.name }} {{ teacher.family }}</p>
            <p class="font_normal">{{ teacher.bio || t('no_bio') }}</p>
          </div>
          <swiper
            :modules="modules"
            :pagination="{ clickable: true }"
            :loop="teacherImages.length > 1"
            class="rounded-5 shadow profile-slider"
          >
            <swiper-slide v-for="(image, index) in teacherImages" :key="index">
              <img :src="image" class="profile-image" alt="Teacher profile image">
            </swiper-slide>
          </swiper>
        </div>

        <div class="d-flex align-items-end justify-content-between gap-4">
          <div class="w-40 d-flex align-items-center justify-content-between gap-3">
            <div class="w-50 d-flex flex-column align-items-center p-2 bg_white rounded-4 shadow_button gap-1">
              <img src="@/assets/imgs/user-_2_.svg" class="icon-size-2" alt="">
              <p>{{ teacher.followers }}</p>
              <p class="font_small">{{ t('followers') }}</p>
            </div>
            <div class="w-50 d-flex flex-column align-items-center p-2 bg_white rounded-4 shadow_button gap-1">
              <img src="@/assets/imgs/share-post.svg" class="icon-size-2" alt="">
              <p>{{ teacher.post_count }}</p>
              <p class="font_small">{{ t('posts') }}</p>
            </div>
          </div>
          <div class="w-60 d-flex align-items-center justify-content-around gap-3">
            <button class="w-50 p-2 bg_orange rounded-pill gap-2 d-flex align-items-center justify-content-evenly shadow_button" @click="goToChat">
              <img src="@/assets/imgs/conversation.svg" class="icon-size-1" alt="">
              <span class="text-white">{{ t('create_chat') }}</span>
            </button>
            <button @click="toggleFollow" class="w-50 p-2 rounded-pill gap-2 d-flex align-items-center justify-content-evenly shadow_button" :class="teacher.is_followed ? 'bg_gray' : 'bg_gradient_theme_to_bottom'">
              <img :src="teacher.is_followed ? require('@/assets/imgs/person.svg') : require('@/assets/imgs/bookmark-white.svg')" class="icon-size-1" alt="">
              <span class="text-white">{{ teacher.is_followed ? t('followed') : t('follow') }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="p-3">
        <p class="bold font_large px-3 mb-2">{{ t('posts') }}</p>
        <div v-if="teacher.teacher_posts && teacher.teacher_posts.length > 0" class="d-flex flex-wrap justify-content-start">
          <div v-for="post in teacher.teacher_posts" :key="post.id" style="width: 33.3%" class="p-1" @click="$router.push({ name: 'TeacherPosts', params: { id: post.id } })">
            <div class="bg_white rounded-4 p-2 shadow-sm d-flex flex-column gap-3 w-100 h-100">
              <img style="height: 10rem; object-fit: cover;" :src="post.pic" class="rounded-4 w-100 shadow-sm" alt="" @error="$event.target.src = require('@/assets/imgs/NoPath - Copy (5).png')">
              <p class="w-100 text-nowrap text-center overflow-hidden font_small">{{ post.caption }}</p>
            </div>
          </div>
        </div>
        <div v-else class="text-center text_color_gray mt-4">
          <p>{{ t('empty_list') }}</p>
        </div>
      </div>
    </template>
  </main>
</template>

<script>
import apiClient, { createFormData } from '@/services/api'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import { Pagination } from 'swiper/modules'

export default {
  name: 'TeacherProfileView',
  components: {
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      teacher: {},
      loading: false
    }
  },
  setup() {
    return {
      modules: [Pagination],
    }
  },
  computed: {
    teacherImages() {
      const imageBaseUrl = 'https://back.ayendaman.org/storage/app/public/uploads/';
      if (!this.teacher || !this.teacher.id) return [];
      
      const images = [];
      const processImage = (pic) => {
        if (pic && pic !== 'nopic.png' && pic !== '' && !pic.includes('nopic')) {
          if (pic.startsWith('http')) {
            images.push(pic);
          } else {
            images.push(imageBaseUrl + pic);
          }
        }
      };

      processImage(this.teacher.profile_pic);
      
      if (images.length === 0) {
        images.push(require('@/assets/imgs/NoPath - Copy (5).png'));
      }
      return images;
    }
  },
  mounted() {
    this.fetchTeacherDetails()
  },
  methods: {
    async fetchTeacherDetails() {
      const teacherId = this.$route.params.id
      if (!teacherId) return

      this.loading = true
      try {
        const userId = localStorage.getItem('user_id')
        const formData = createFormData({
          user_id: userId,
          teacher_id: teacherId
        })
        const response = await apiClient.post('/teachers_profile_details', formData)
        if (response.data.status === 'ok' && response.data.data) {
          this.teacher = response.data.data
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async toggleFollow() {
      if (!this.teacher.id) return
      try {
        const userId = localStorage.getItem('user_id')
        const formData = createFormData({
          user_id: userId,
          teacher_id: this.teacher.id
        })
        const response = await apiClient.post('/follow_teacher', formData)
        if (response.data.status === 'ok') {
          if (response.data.event === 'teacher_follow_successfully') {
            this.teacher.is_followed = true
            this.teacher.followers++
          } else if (response.data.event === 'teacher_unfollow_successfully') {
            this.teacher.is_followed = false
            this.teacher.followers--
          }
        }
      } catch (error) {
        console.error(error)
      }
    },
    goToChat() {
      this.$router.push({ 
        name: 'Chat', 
        params: { teacherId: this.teacher.id },
        query: { teacherName: `${this.teacher.name} ${this.teacher.family}` }
      })
    }
  }
}
</script>

<style scoped>
.profile-slider {
  border: 5px solid white;
  width: 13rem;
  height: 13rem;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cursor-pointer {
  cursor: pointer;
}
</style>